import { generateJobListingsUrlObject } from '@/app/utils/jobListingHelper';
import { useAnalytics } from '@/app/utils/useAnalytics';
import LeftTears from '@/modules/HomePageV2/assets/leftTears.svg';
import RightTears from '@/modules/HomePageV2/assets/rightTears.svg';
import { ILocation, ISchedulerLocation } from '@/services/types';
import { JobSearchGrade, WorkType } from '@/types/jobs';
import { DEFAULT_RADIUS } from '@/utils/jobSearchBoxOptions';
import { setGradeCookie, setLocationCookie } from '@/utils/jobSearchCookies';
import { useRouter } from 'next/dist/client/router';
import React, { useState } from 'react';

import styles from './JobSearchHeader.module.scss';
import { MedrecruitStats } from './components/MedrecruitStats/MedrecruitStats';
import { GradeSelect } from './components/formInputs/GradeSelect/GradeSelect';
import { LocationAutoComplete } from './components/formInputs/LocationAutoComplete/LocationAutoComplete';
import { SearchButton } from './components/formInputs/SearchButton/SearchButton';
import { WorkTypeCheckboxGroup } from './components/formInputs/WorkTypeCheckboxGroup/WorkTypeCheckboxGroup';

const getWorkTypesSelected = (workTypes: WorkType[]) => {
  if (workTypes.length === 1) {
    return workTypes[0];
  }
  return workTypes.length ? 'both' : 'none';
};

interface JobSearchHeaderProps {
  // Live job total, already rounded/formatted - shares its source with the homepage <title>.
  jobCount?: string | null;
}

export const JobSearchHeader = ({ jobCount }: JobSearchHeaderProps) => {
  const router = useRouter();
  const track = useAnalytics();

  // form state
  const [selectedGrade, setSelectedGrade] = useState<JobSearchGrade | undefined>(undefined);
  const [selectedWorkTypes, setSelectedWorkTypes] = useState<WorkType[]>([]);
  const [locationCoordinate, setLocationCoordinate] = useState<
    ILocation | ISchedulerLocation | null
  >(null);
  const [isSearching, setIsSearching] = useState(false);
  // end of form state

  const handleSearch = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setIsSearching(true);
    const lon = (locationCoordinate as ILocation)?.position?.lon.toString();
    const lat = (locationCoordinate as ILocation)?.position?.lat.toString();

    const isOneWorkTypeSelected = selectedWorkTypes.length === 1;

    const search = {
      grade: selectedGrade,
      location: (locationCoordinate as ISchedulerLocation)?.searchLocations?.join(';'),
      lon,
      lat,
      locationName: locationCoordinate?.name,
      radius: lat && lon ? DEFAULT_RADIUS : undefined,
      page: 1,
      workType: isOneWorkTypeSelected ? selectedWorkTypes[0] : undefined,
    };

    if (selectedGrade) {
      setGradeCookie(selectedGrade);
    }

    if (locationCoordinate) {
      setLocationCookie(locationCoordinate);
    }

    track('Job Search', {
      ...search,
      autocompleteLocationSelectedType: search.lon ? 'specific' : 'scheduler',
      autocompleteLocationRadius: null,
      jobSearchSimple: true,
      workTypesSelected: getWorkTypesSelected(selectedWorkTypes),
    });

    const urlObject = generateJobListingsUrlObject(search);

    return router.push(urlObject);
  };

  return (
    <div data-testid="job-search-header-v2" className={styles.jobSearchHeaderContainer}>
      <LeftTears className={styles.leftTears} />
      <RightTears className={styles.rightTears} />
      <div>
        <h1 className={styles.title}>
          {jobCount ? `Search over ${jobCount} doctor jobs in` : 'Search doctor jobs in'}
          <br /> Australia and New Zealand.
        </h1>
      </div>
      <form className={styles.searchForm} onSubmit={handleSearch}>
        <GradeSelect value={selectedGrade} onChange={setSelectedGrade} />
        <WorkTypeCheckboxGroup value={selectedWorkTypes} onChange={setSelectedWorkTypes} />
        <LocationAutoComplete value={locationCoordinate} onChange={setLocationCoordinate} />
        <SearchButton loading={isSearching} />
      </form>
      <MedrecruitStats />
    </div>
  );
};
